import styled, { css } from 'styled-components'
import { RadioButton as NotinoRadioButton, theme } from '@notino/react-styleguide'
import UsersIcon from '../../assets/icons/UsersIcon'
import { NotinoLabelLarge, NotinoLabelLarge400, NotinoLabelRegular400 } from '../../styles/helpers'
import CheckIcon from '../../assets/icons/CheckIcon'

const getRadioButtonBorderColor = ($checked: boolean, $disabled?: boolean) => {
	if ($disabled && !$checked) {
		return theme.color.border.disabled
	}

	return $checked ? theme.color.common.black : theme.color.border.divider
}

export const RadioButtonsWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`

export const OptionGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`

export const OptionGroupLabel = styled.div`
	${NotinoLabelLarge};
	color: ${theme.color.text.primary};
	text-transform: capitalize;
`

export const OptionGroupOptions = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`

export const RadioButton = styled(NotinoRadioButton)<{ $inputStyle: string; $checked: boolean; $disabled?: boolean }>`
	display: flex;
	align-items: center;
	min-height: 92px;
	border: 1px solid ${({ $checked, $disabled }) => getRadioButtonBorderColor($checked, $disabled)};
	padding: 20px;
	position: relative;
	border-radius: 4px;
	background: ${theme.color.common.white};
	transition: all 0.2s ease;
	cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};

	& > *:last-child {
		position: absolute;
		right: 20px;
		top: 50%;
		transform: translateY(-50%);
		transition: border-color 0.3s ease;
		left: auto;
	}

	&:has(> input[type='radio']:focus-visible) {
		outline: 2px solid ${theme.color.common.black};
		outline-offset: 2px;
	}

	input[type='radio']:focus-visible ~ span {
		border-color: ${theme.color.common.black};
	}

	${({ $disabled }) =>
		!$disabled &&
		css`
			&:hover {
				border-color: ${theme.color.common.black};
			}
		`}

	${({ $disabled, $checked }) =>
		$disabled &&
		!$checked &&
		css`
			& > *:last-child {
				border-color: ${theme.color.border.disabled};
				background-color: ${theme.color.common.white};
			}
		`}

	${({ $disabled, $checked }) =>
		$disabled &&
		$checked &&
		css`
			input[type='radio']:checked:disabled ~ span {
				border-color: ${theme.color.border.selected};
				background-color: ${theme.color.common.white};

				&::after {
					display: block;
					background-color: ${theme.color.background.inverse};
				}
			}
		`}
`

export const SlotContent = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	justify-content: center;
	gap: 4px;
`

export const SlotHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
`

export const SlotTime = styled.span<{ $checked: boolean; $disabled?: boolean }>`
	${NotinoLabelLarge400};
	color: ${({ $disabled }) => ($disabled ? theme.color.text.disabled : theme.color.text.primary)};
`

export const AlreadyBookedWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
`

export const GreenCheckIcon = styled(CheckIcon)<{ $disabled?: boolean }>`
	flex-shrink: 0;
	width: 16px;
	height: 16px;
	color: ${({ $disabled }) => ($disabled ? theme.color.text.disabled : theme.color.text.positive)};
`

export const AlreadyBookedText = styled.span<{ $disabled?: boolean }>`
	${NotinoLabelRegular400};
	color: ${({ $disabled }) => ($disabled ? theme.color.text.disabled : theme.color.text.secondary)};
`

export const SlotFooter = styled.div`
	display: flex;
	align-items: center;
	color: ${theme.color.text.tertiary};
	margin-top: 2px;
	gap: 8px;
`

export const SpotsLeft = styled.span<{ $disabled?: boolean }>`
	${NotinoLabelRegular400};
	color: ${({ $disabled }) => ($disabled ? theme.color.text.disabled : theme.color.text.secondary)};
`

export const Capacity = styled.span<{ $disabled?: boolean }>`
	display: flex;
	align-items: center;
	${NotinoLabelRegular400};
	gap: 4px;
	color: ${({ $disabled }) => ($disabled ? theme.color.text.disabled : theme.color.text.tertiary)};
`

export const PeopleIcon = styled(UsersIcon)<{ $disabled?: boolean }>`
	width: 16px;
	height: 16px;
	color: ${({ $disabled }) => ($disabled ? theme.color.text.disabled : theme.color.text.tertiary)};
`
